/**
 * The no-flash theme script.
 *
 * It runs in `index.html` before any stylesheet applies, so the first paint is already the right
 * theme: no white flash for a dark-theme user. It is exported as a string rather than a module
 * because it must be inlined, and it is deliberately tiny and dependency-free.
 */

export const themeStorageKey = 'ccp.theme';
export const brandStorageKey = 'ccp.brand';
/** Kept here, not imported, because this file is inlined as a string and must stay dependency-free. */
const densityKey = 'ccp.density';

/** What a person chose. `system` follows the operating system and re-resolves when it changes. */
export type ThemePreference = 'light' | 'dark' | 'system';
/** What is actually applied. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * The script source to inline in `<head>`, before the stylesheet link.
 *
 * ```html
 * <script>__CCP_THEME_SCRIPT__</script>
 * ```
 *
 * In system mode it sets no `data-theme`, so the `prefers-color-scheme` block in `tokens.css`
 * resolves; that keeps one rule in CSS rather than two sources of truth. Density is set here for
 * the same reason the theme is: a compact user who saw a comfortable first paint would watch every
 * control on the screen shrink a moment later.
 */
export const themeScript = `(function(){try{
var k=${JSON.stringify(themeStorageKey)},b=${JSON.stringify(brandStorageKey)},n=${JSON.stringify(densityKey)};
var p=localStorage.getItem(k)||'system';
var r=document.documentElement;
if(p==='light'||p==='dark'){r.setAttribute('data-theme',p);r.style.colorScheme=p;}
else{r.removeAttribute('data-theme');r.style.colorScheme='light dark';}
var d=localStorage.getItem(b);if(d){r.setAttribute('data-brand',d);}
var y=localStorage.getItem(n);if(y==='compact'){r.setAttribute('data-density',y);}
}catch(e){}})();`;

/** Reads the stored preference; `system` when nothing is stored or storage is unavailable. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(themeStorageKey);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // A private window or blocked storage is not an error: the default preference applies.
    return 'system';
  }
}

/** Turns a preference into the theme to apply, consulting the system only for `system`. */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') {
    return preference;
  }
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
    ? 'dark'
    : 'light';
}

/** Applies a preference to the document, mirroring the inline script's rules exactly. */
export function applyTheme(preference: ThemePreference, root?: HTMLElement): void {
  const element = root ?? globalThis.document?.documentElement;
  if (element === undefined) {
    return;
  }
  if (preference === 'system') {
    element.removeAttribute('data-theme');
    element.style.colorScheme = 'light dark';
    return;
  }
  element.setAttribute('data-theme', preference);
  element.style.colorScheme = preference;
}
